import { z } from "zod";

export const AccountingDayStatusSchema = z.enum(["open", "closed"]);
export type AccountingDayStatus = z.infer<typeof AccountingDayStatusSchema>;

export const CashMovementTypeSchema = z.enum([
  "expense",
  "cash_in",
  "investor_payout",
  "safekeeping",
]);
export type CashMovementType = z.infer<typeof CashMovementTypeSchema>;

export const ExpenseCategorySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(64),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
});
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

export const PartnerSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(128),
  sharePercent: z.number().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
});
export type Partner = z.infer<typeof PartnerSchema>;

export const PartnerBalanceSchema = z.object({
  partner: PartnerSchema,
  totalPaidOutCents: z.number().int().nonnegative(),
  movementCount: z.number().int().nonnegative(),
});
export type PartnerBalance = z.infer<typeof PartnerBalanceSchema>;

export const CashMovementSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  dayId: z.string().uuid(),
  type: CashMovementTypeSchema,
  amountCents: z.number().int().positive(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  partnerId: z.string().uuid().nullable(),
  partnerName: z.string().nullable(),
  heldByUserId: z.string().nullable(),
  heldByName: z.string().nullable(),
  note: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});
export type CashMovement = z.infer<typeof CashMovementSchema>;

/** Result of computeDayTotals. varianceCents is null until cash is counted. */
export const DayTotalsSchema = z.object({
  revenueCents: z.number().int(),
  refundCents: z.number().int(),
  cardRevenueCents: z.number().int(),
  cardRefundCents: z.number().int(),
  expenseCents: z.number().int(),
  cashInCents: z.number().int(),
  investorOutCents: z.number().int(),
  safekeepingCents: z.number().int(),
  safekeepingReturnCents: z.number().int(),
  expectedCashCents: z.number().int(),
  varianceCents: z.number().int().nullable(),
  netCashCents: z.number().int(),
  orderCount: z.number().int().nonnegative(),
});
export type DayTotals = z.infer<typeof DayTotalsSchema>;

export const AccountingDaySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  businessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  status: AccountingDayStatusSchema,
  openingFloatCents: z.number().int().nonnegative(),
  openedAt: z.string().datetime(),
  openedByUserId: z.string().uuid().nullable(),
  closedAt: z.string().datetime().nullable(),
  closedByUserId: z.string().uuid().nullable(),
  countedCashCents: z.number().int().nullable(),
  notes: z.string().nullable(),
  reopenCount: z.number().int().nonnegative(),
  totals: DayTotalsSchema,
});
export type AccountingDay = z.infer<typeof AccountingDaySchema>;

/** A day plus everything the sheet screen needs in one round trip. */
export const AccountingDayViewSchema = z.object({
  day: AccountingDaySchema,
  movements: z.array(CashMovementSchema),
});
export type AccountingDayView = z.infer<typeof AccountingDayViewSchema>;

export const UnattributedOrderSchema = z.object({
  id: z.string().uuid(),
  customerName: z.string(),
  totalCents: z.number().int(),
  deliveredAt: z.string().datetime(),
});
export type UnattributedOrder = z.infer<typeof UnattributedOrderSchema>;

export const AttachOrderResultSchema = z.object({
  orderId: z.string().uuid(),
  dayId: z.string().uuid(),
});
export type AttachOrderResult = z.infer<typeof AttachOrderResultSchema>;

export const AccountingSummarySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dayCount: z.number().int().nonnegative(),
  totals: DayTotalsSchema,
  expenseByCategory: z.array(
    z.object({
      categoryId: z.string().uuid().nullable(),
      categoryName: z.string(),
      amountCents: z.number().int().nonnegative(),
    }),
  ),
  investorByPartner: z.array(
    z.object({
      partnerId: z.string().uuid().nullable(),
      partnerName: z.string(),
      amountCents: z.number().int().nonnegative(),
    }),
  ),
  settlements: z.object({
    expenseCents: z.number().int().nonnegative(),
    payoutCents: z.number().int().nonnegative(),
    returnCents: z.number().int().nonnegative(),
  }),
  heldCents: z.number().int().nonnegative(),
});
export type AccountingSummary = z.infer<typeof AccountingSummarySchema>;

export const SafekeepingSettlementKindSchema = z.enum(["return", "payout", "expense"]);
export type SafekeepingSettlementKind = z.infer<typeof SafekeepingSettlementKindSchema>;

export const SafekeepingSettlementSchema = z.object({
  id: z.string().uuid(),
  movementId: z.string().uuid(),
  kind: SafekeepingSettlementKindSchema,
  amountCents: z.number().int().positive(),
  partnerId: z.string().uuid().nullable(),
  partnerName: z.string().nullable(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  dayId: z.string().uuid().nullable(),
  settledOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  note: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type SafekeepingSettlement = z.infer<typeof SafekeepingSettlementSchema>;

export const SafekeepingHoldSchema = z.object({
  movement: CashMovementSchema,
  businessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  settledCents: z.number().int().nonnegative(),
  remainingCents: z.number().int().nonnegative(),
  settlements: z.array(SafekeepingSettlementSchema),
});
export type SafekeepingHold = z.infer<typeof SafekeepingHoldSchema>;

export const SafekeepingOverviewSchema = z.object({
  heldCents: z.number().int().nonnegative(),
  heldByHolder: z.array(
    z.object({
      userId: z.string().nullable(),
      name: z.string().nullable(),
      amountCents: z.number().int().nonnegative(),
    }),
  ),
  open: z.array(SafekeepingHoldSchema),
  settled: z.array(SafekeepingHoldSchema),
});
export type SafekeepingOverview = z.infer<typeof SafekeepingOverviewSchema>;
